import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel as ChakraFormLabel,
  FormErrorMessage,
  Heading,
  Input,
  Spinner,
  Text,
  Flex,
  SimpleGrid,
  Icon,
} from "@chakra-ui/react";
import { FiDollarSign, FiEdit2, FiPlusCircle, FiSave } from "react-icons/fi";
import styled from "@emotion/styled";
import { useSetRecoilState } from "recoil";
import { toastState } from "atoms/toastState";
import useAxios from "axios-hooks";
import logger from "infra/logger";
import * as yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";

const Configurations = (props) => {
  const { station } = props;

  // State for payout configuration
  const [payoutConfig, setPayoutConfig] = useState(null);
  const [isEditing, setIsEditing] = useState(false);

  // Validation schema
  const validationSchema = yup.object().shape({
    ElectricityUnitPrice: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Electricity unit price is required"),
    DemandCharge: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Demand charge is required"),
    NetworkCharge: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      // .nullable()
      // .transform((value, originalValue) => originalValue === "" ? null : value)
      .optional(),
    HostingChargePerUnit: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Hosting charge is required"),
    UrzzaChargePerUnit: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Platform charge is required"),
    MinAmount: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Minimum amount is required"),
    MinUnits: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Minimum units is required"),
    AgreedPrice: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Agreed price is required"),
    UrzzaFixedCharge: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("Platform fixed charge is required"),
    ExternalFixedIncome: yup
      .number()
      .typeError("Must be a number")
      .min(0, "Must be at least 0")
      .required("External fixed income is required"),
  });

  // Form field configurations
  const formFields = [
    {
      name: "ElectricityUnitPrice",
      label: "Electricity Unit Price (₹)",
      placeholder: "Enter price per unit",
      type: "number",
      step: "0.01",
    },
    {
      name: "DemandCharge",
      label: "Demand Charge (₹)",
      placeholder: "Enter demand charge",
      type: "number",
      step: "0.01",
    },
    {
      name: "NetworkCharge",
      label: "Network Charge (₹)",
      placeholder: "Enter network charge (optional)",
      type: "number",
      step: "0.01",
    },
    {
      name: "HostingChargePerUnit",
      label: "Hosting Charge Per Unit (₹)",
      placeholder: "Enter hosting charge per unit",
      type: "number",
      step: "0.01",
    },
    {
      name: "UrzzaChargePerUnit",
      label: "Platform Charge Per Unit (₹)",
      placeholder: "Enter platform charge per unit",
      type: "number",
      step: "0.01",
    },
    {
      name: "MinAmount",
      label: "Minimum Amount (₹)",
      placeholder: "Enter minimum amount",
      type: "number",
      step: "0.01",
    },
    {
      name: "MinUnits",
      label: "Minimum Units",
      placeholder: "Enter minimum units",
      type: "number",
      step: "0.01",
    },
    {
      name: "AgreedPrice",
      label: "Agreed Price (₹)",
      placeholder: "Enter agreed price",
      type: "number",
      step: "0.01",
    },
    {
      name: "UrzzaFixedCharge",
      label: "Platform Fixed Charge (₹)",
      placeholder: "Enter platform fixed charge",
      type: "number",
      step: "0.01",
    },
    {
      name: "ExternalFixedIncome",
      label: "External Fixed Income (₹)",
      placeholder: "Enter external fixed income",
      type: "number",
      step: "0.01",
    },
  ];

  // React Hook Form
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm({
    resolver: yupResolver(validationSchema),
    mode: "onChange",
    defaultValues: {
      ElectricityUnitPrice: 0,
      DemandCharge: 0,
      NetworkCharge: 0,
      HostingChargePerUnit: 0,
      UrzzaChargePerUnit: 0,
      MinAmount: 0,
      MinUnits: 0,
      AgreedPrice: 0,
      UrzzaFixedCharge: 0,
      ExternalFixedIncome: 0,
    },
  });

  // Recoil state for toast notifications
  const setToast = useSetRecoilState(toastState);

  // API calls for fetching, creating, and updating payout config
  const [{ loading: configLoading }, fetchConfig] = useAxios(
    { method: "GET", url: `/api/stations/payoutConfig/${station.Id}` },
    { manual: true },
  );

  const [{ loading: createLoading }, createConfig] = useAxios(
    { method: "POST", url: `/api/stations/payoutConfig/${station.Id}` },
    { manual: true },
  );

  const [{ loading: updateLoading }, updateConfig] = useAxios(
    {
      method: "PATCH",
      url: `/api/stations/payoutConfig/${payoutConfig?.Id}`,
    },
    { manual: true },
  );

  // Fetch payout configuration when component mounts
  const getPayoutConfig = useCallback(async () => {
    try {
      const response = await fetchConfig();
      if (response.data && response.data.data) {
        setPayoutConfig(response.data.data);

        // Reset form with fetched values
        reset({
          ElectricityUnitPrice: response.data.data.ElectricityUnitPrice || 0,
          DemandCharge: response.data.data.DemandCharge || 0,
          NetworkCharge: response.data.data.NetworkCharge || 0,
          HostingChargePerUnit: response.data.data.HostingChargePerUnit || 0,
          UrzzaChargePerUnit: response.data.data.UrzzaChargePerUnit || 0,
          MinAmount: response.data.data.MinAmount || 0,
          MinUnits: response.data.data.MinUnits || 0,
          AgreedPrice: response.data.data.AgreedPrice || 0,
          UrzzaFixedCharge: response.data.data.UrzzaFixedCharge || 0,
          ExternalFixedIncome: response.data.data.ExternalFixedIncome || 0,
        });
      }
    } catch (error) {
      if (error.code === "ERR_CANCELED") {
        return;
      }
      logger.log(error);
      setToast({
        isOpen: true,
        title: "Error fetching payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  }, [fetchConfig, reset, setToast]);

  // Create new payout configuration
  const handleCreateConfig = async (data) => {
    try {
      const payload = {
        StationId: station.StationId,
        ElectricityUnitPrice: parseFloat(data.ElectricityUnitPrice),
        DemandCharge: parseFloat(data.DemandCharge),
        NetworkCharge: data.NetworkCharge !== null && data.NetworkCharge !== "" ? parseFloat(data.NetworkCharge) : 0,
        HostingChargePerUnit: parseFloat(data.HostingChargePerUnit),
        UrzzaChargePerUnit: parseFloat(data.UrzzaChargePerUnit),
        MinAmount: parseFloat(data.MinAmount),
        MinUnits: parseFloat(data.MinUnits),
        AgreedPrice: parseFloat(data.AgreedPrice),
        UrzzaFixedCharge: parseFloat(data.UrzzaFixedCharge),
        ExternalFixedIncome: parseFloat(data.ExternalFixedIncome),
      };

      const response = await createConfig({ data: payload });
      if (response.data && response.data.data) {
        setPayoutConfig(response.data.data);
        setIsEditing(false);
        setToast({
          isOpen: true,
          title: "Payout configuration created successfully",
          status: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      logger.error("Error creating payout config:", error);
      setToast({
        isOpen: true,
        title: "Error creating payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Update existing payout configuration
  const handleUpdateConfig = async (data) => {
    try {
      // Create a payload with only modified fields
      const payload = { StationId: station.StationId };
      let hasChanges = false;

      // Check each field for changes
      if (
        payoutConfig.ElectricityUnitPrice !==
        parseFloat(data.ElectricityUnitPrice)
      ) {
        payload.ElectricityUnitPrice = parseFloat(data.ElectricityUnitPrice);
        hasChanges = true;
      }

      if (payoutConfig.DemandCharge !== parseFloat(data.DemandCharge)) {
        payload.DemandCharge = parseFloat(data.DemandCharge);
        hasChanges = true;
      }

      const newNetworkCharge =
        data.NetworkCharge !== null &&
        data.NetworkCharge !== "" &&
        data.NetworkCharge !== undefined
          ? parseFloat(data.NetworkCharge)
          : null;
      if (payoutConfig.NetworkCharge !== newNetworkCharge) {
        payload.NetworkCharge = newNetworkCharge;
        hasChanges = true;
      }

      if (
        payoutConfig.HostingChargePerUnit !==
        parseFloat(data.HostingChargePerUnit)
      ) {
        payload.HostingChargePerUnit = parseFloat(data.HostingChargePerUnit);
        hasChanges = true;
      }

      if (
        payoutConfig.UrzzaChargePerUnit !== parseFloat(data.UrzzaChargePerUnit)
      ) {
        payload.UrzzaChargePerUnit = parseFloat(data.UrzzaChargePerUnit);
        hasChanges = true;
      }

      if (payoutConfig.MinAmount !== parseFloat(data.MinAmount)) {
        payload.MinAmount = parseFloat(data.MinAmount);
        hasChanges = true;
      }

      if (payoutConfig.MinUnits !== parseFloat(data.MinUnits)) {
        payload.MinUnits = parseFloat(data.MinUnits);
        hasChanges = true;
      }

      if (payoutConfig.AgreedPrice !== parseFloat(data.AgreedPrice)) {
        payload.AgreedPrice = parseFloat(data.AgreedPrice);
        hasChanges = true;
      }

      if (payoutConfig.UrzzaFixedCharge !== parseFloat(data.UrzzaFixedCharge)) {
        payload.UrzzaFixedCharge = parseFloat(data.UrzzaFixedCharge);
        hasChanges = true;
      }

      if (
        payoutConfig.ExternalFixedIncome !==
        parseFloat(data.ExternalFixedIncome)
      ) {
        payload.ExternalFixedIncome = parseFloat(data.ExternalFixedIncome);
        hasChanges = true;
      }

      // If no changes, show toast and return
      if (!hasChanges) {
        setIsEditing(false);
        setToast({
          isOpen: true,
          title: "Nothing to update",
          status: "warning",
          duration: 5000,
        });
        return;
      }

      // Send update request with only changed fields
      const response = await updateConfig({ data: payload });
      if (response.data && response.data.data) {
        setPayoutConfig(response.data.data);
        setIsEditing(false);
        setToast({
          isOpen: true,
          title: "Payout configuration updated successfully",
          status: "success",
          duration: 5000,
        });
      }
    } catch (error) {
      logger.error("Error updating payout config:", error);
      setToast({
        isOpen: true,
        title: "Error updating payout configuration",
        status: "error",
        duration: 5000,
      });
    }
  };

  // Handle form submission
  const onSubmit = (data) => {
    if (payoutConfig) {
      handleUpdateConfig(data);
    } else {
      handleCreateConfig(data);
    }
  };

  useEffect(() => {
    if (station && station.Id) {
      getPayoutConfig();
    }
  }, [station, getPayoutConfig]);

  // Populate form with existing values when editing mode is enabled
  useEffect(() => {
    if (isEditing && payoutConfig) {
      reset({
        ElectricityUnitPrice: payoutConfig.ElectricityUnitPrice || 0,
        DemandCharge: payoutConfig.DemandCharge || 0,
        NetworkCharge: payoutConfig.NetworkCharge ?? "",
        HostingChargePerUnit: payoutConfig.HostingChargePerUnit || 0,
        UrzzaChargePerUnit: payoutConfig.UrzzaChargePerUnit || 0,
        MinAmount: payoutConfig.MinAmount || 0,
        MinUnits: payoutConfig.MinUnits || 0,
        AgreedPrice: payoutConfig.AgreedPrice || 0,
        UrzzaFixedCharge: payoutConfig.UrzzaFixedCharge || 0,
        ExternalFixedIncome: payoutConfig.ExternalFixedIncome || 0,
      });
    }
  }, [isEditing, payoutConfig, reset]);

  // Loading state
  const isLoading = configLoading || createLoading || updateLoading;

  // Styled components for glass morphism effect
  const GlassCard = styled(Box)`
    background: rgba(255, 255, 255, 0.6);
    backdrop-filter: blur(12px);
    border: 1px solid rgba(35, 169, 121, 0.15);
    border-radius: 20px;
    padding: 24px;
    transition: all 0.3s ease;
    box-shadow: 0 8px 32px rgba(31, 38, 135, 0.1);

    &:hover {
      box-shadow: 0 8px 32px rgba(31, 38, 135, 0.15);
    }
  `;

  const StyledButton = styled(Button)`
    background: ${(props) =>
      props.variant === "outline" ? "transparent" : "#23A979"};
    color: ${(props) => (props.variant === "outline" ? "#23A979" : "white")};
    border: ${(props) =>
      props.variant === "outline" ? "1px solid #23A979" : "none"};
    transition: all 0.3s ease;

    &:hover {
      background: ${(props) =>
        props.variant === "outline" ? "rgba(35, 169, 121, 0.1)" : "#e06826"};
      transform: translateY(-2px);
    }

    &:active {
      transform: translateY(0);
    }
  `;

  const FormLabel = styled(ChakraFormLabel)`
    color: #4a5568;
    font-weight: 600;
    font-size: 14px;
    margin-bottom: 6px;
    position: relative;
    display: inline-block;

    &:after {
      content: "";
      position: absolute;
      bottom: -3px;
      left: 0;
      width: 16px;
      height: 2px;
      background: rgba(35, 169, 121, 0.5);
      border-radius: 1px;
    }
  `;

  const InputGroup = styled(Box)`
    margin-bottom: 18px;
    position: relative;
  `;

  const StyledInput = styled(Input)`
    background: rgba(255, 255, 255, 0.7);
    border: 1px solid rgba(35, 169, 121, 0.2);
    border-radius: 8px;
    height: 40px;
    max-width: 200px;
    transition: all 0.2s ease;

    &:hover {
      background: rgba(255, 255, 255, 0.8);
      border-color: rgba(35, 169, 121, 0.3);
    }

    &:focus {
      background: rgba(255, 255, 255, 0.9);
      border-color: #23A979;
      box-shadow: 0 0 0 1px rgba(35, 169, 121, 0.3);
    }

    &::placeholder {
      color: rgba(160, 174, 192, 0.7);
    }
  `;

  const SectionTitle = styled(Heading)`
    color: #2d3748;
    position: relative;
    display: inline-block;
    margin-bottom: 24px;

    &:after {
      content: "";
      position: absolute;
      bottom: -8px;
      left: 0;
      width: 40px;
      height: 4px;
      background: #23A979;
      border-radius: 2px;
    }
  `;

  // Check if user has permission to edit (currently unused but kept for future use)
  // const hasEditPermission = useMemo(() => {
  //   return (
  //     loggedInUser &&
  //     (loggedInUser.role === "admin" || loggedInUser.role === "superadmin")
  //   );
  // }, [loggedInUser]);

  // Render empty state when no config exists
  if (!isLoading && !payoutConfig && !isEditing) {
    return (
      <GlassCard textAlign="center" py={10}>
        <Box mb={6}>
          <Icon as={FiDollarSign} boxSize={12} color="gray.400" />
        </Box>
        <Heading size="md" mb={4} color="gray.700">
          No Payout Configuration
        </Heading>
        <Text mb={6} color="gray.500">
          This charging station doesn't have a payout configuration yet.
        </Text>
        <StyledButton
          leftIcon={<FiPlusCircle />}
          onClick={() => setIsEditing(true)}
          isDisabled={isLoading}
        >
          Create Configuration
        </StyledButton>
      </GlassCard>
    );
  }

  // Render loading state
  if (isLoading && !payoutConfig && !isEditing) {
    return (
      <GlassCard
        display="flex"
        justifyContent="center"
        alignItems="center"
        py={10}
      >
        <Spinner
          thickness="4px"
          speed="0.65s"
          emptyColor="gray.200"
          color="brand.500"
          size="xl"
        />
      </GlassCard>
    );
  }

  // Render form for creating/editing
  if (isEditing || (!payoutConfig && !isLoading)) {
    return (
      <GlassCard>
        <SectionTitle size="md">
          {payoutConfig
            ? "Edit Payout Configuration"
            : "Create Payout Configuration"}
        </SectionTitle>

        <form onSubmit={handleSubmit(onSubmit)}>
          <SimpleGrid
            columns={{ base: 1, sm: 2, lg: 3, xl: 4 }}
            spacing={6}
            maxW="100%"
          >
            {formFields.map((field) => (
              <InputGroup key={field.name}>
                <Flex direction="column">
                  <FormLabel htmlFor={field.name}>{field.label}</FormLabel>
                  <Controller
                    name={field.name}
                    control={control}
                    render={({ field: { onChange, onBlur, value, ref } }) => (
                      <FormControl isInvalid={errors[field.name]}>
                        <StyledInput
                          id={field.name}
                          type={field.type}
                          step={field.step}
                          placeholder={field.placeholder}
                          onChange={(e) => {
                            const val = e.target.value;
                            onChange(val === "" ? "" : parseFloat(val));
                          }}
                          onBlur={onBlur}
                          value={value === 0 ? "0" : value}
                          ref={ref}
                        />
                        {errors[field.name] && (
                          <FormErrorMessage>
                            {errors[field.name].message}
                          </FormErrorMessage>
                        )}
                      </FormControl>
                    )}
                  />
                </Flex>
              </InputGroup>
            ))}
          </SimpleGrid>

          <Flex justifyContent="flex-end" mt={4} gap={3}>
            <StyledButton
              variant="outline"
              onClick={() => {
                setIsEditing(false);
                if (!payoutConfig) {
                  reset({
                    ElectricityUnitPrice: 0,
                    DemandCharge: 0,
                    NetworkCharge: "",
                    HostingChargePerUnit: 0,
                    UrzzaChargePerUnit: 0,
                    MinAmount: 0,
                    MinUnits: 0,
                    AgreedPrice: 0,
                    UrzzaFixedCharge: 0,
                    ExternalFixedIncome: 0,
                  });
                } else {
                  reset({
                    ElectricityUnitPrice:
                      payoutConfig.ElectricityUnitPrice || 0,
                    DemandCharge: payoutConfig.DemandCharge || 0,
                    NetworkCharge: payoutConfig.NetworkCharge ?? "",
                    HostingChargePerUnit:
                      payoutConfig.HostingChargePerUnit || 0,
                    UrzzaChargePerUnit: payoutConfig.UrzzaChargePerUnit || 0,
                    MinAmount: payoutConfig.MinAmount || 0,
                    MinUnits: payoutConfig.MinUnits || 0,
                    AgreedPrice: payoutConfig.AgreedPrice || 0,
                    UrzzaFixedCharge: payoutConfig.UrzzaFixedCharge || 0,
                    ExternalFixedIncome: payoutConfig.ExternalFixedIncome || 0,
                  });
                }
              }}
            >
              Cancel
            </StyledButton>
            <StyledButton
              type="submit"
              isLoading={isLoading}
              leftIcon={<FiSave />}
            >
              {payoutConfig ? "Update" : "Create"}
            </StyledButton>
          </Flex>
        </form>
      </GlassCard>
    );
  }

  // Render view mode (when config exists and not editing)
  return (
    <GlassCard>
      <Flex justifyContent="space-between" alignItems="center" mb={6}>
        <SectionTitle size="md">Payout Configuration</SectionTitle>
        <StyledButton
          leftIcon={<FiEdit2 />}
          variant="outline"
          onClick={() => setIsEditing(true)}
          size="sm"
        >
          Edit
        </StyledButton>
      </Flex>

      <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Electricity Unit Price
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.ElectricityUnitPrice?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Demand Charge
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.DemandCharge?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Network Charge
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            {payoutConfig.NetworkCharge !== null &&
            payoutConfig.NetworkCharge !== undefined
              ? `₹${payoutConfig.NetworkCharge?.toFixed(2)}`
              : "N/A"}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Hosting Charge Per Unit
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.HostingChargePerUnit?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Urzza Charge Per Unit
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.UrzzaChargePerUnit?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Minimum Amount
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.MinAmount?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Minimum Units
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            {payoutConfig.MinUnits?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Agreed Price
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.AgreedPrice?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            Urzza Fixed Charge
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.UrzzaFixedCharge?.toFixed(2)}
          </Text>
        </Box>

        <Box>
          <Text fontWeight="semibold" color="gray.500" fontSize="sm">
            External Fixed Income
          </Text>
          <Text fontWeight="bold" fontSize="lg">
            ₹{payoutConfig.ExternalFixedIncome?.toFixed(2)}
          </Text>
        </Box>
      </SimpleGrid>
    </GlassCard>
  );
};

export default Configurations;
